import { EditOutlined } from '@ant-design/icons';
import { Button, Form, Spin, Tooltip } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

type AddressDetailsRowProps = {
  label: string;
  displayValue?: string | React.ReactNode;
  loading?: boolean;
  editable?: boolean;
  formContent?: React.ReactNode;
  fieldErrorName?: string;
  fieldErrorMessage?: string;
  disableSave?: (values: unknown) => boolean;
  onFinish?: (values: unknown) => Promise<boolean | void> | boolean | void;
};

const AddressDetailsRow: React.FC<AddressDetailsRowProps> = ({
  label,
  displayValue,
  loading,
  editable,
  formContent,
  fieldErrorName,
  fieldErrorMessage,
  disableSave,
  onFinish,
}) => {
  const [form] = Form.useForm();
  const watchedValues = Form.useWatch([], form);
  const [updating, setUpdating] = useState<boolean>(false);
  const [editing, setEditing] = useState<boolean>(false);

  const { t } = useTranslation();

  const submit = async (values: unknown) => {
    setUpdating(true);
    let closeEditor = true;
    try {
      const submitResult = await onFinish?.(values);
      closeEditor = submitResult !== false;

      if (!closeEditor && fieldErrorName && fieldErrorMessage) {
        form.setFields([
          {
            name: fieldErrorName,
            errors: [fieldErrorMessage],
          },
        ]);
      }
    } finally {
      setUpdating(false);
      if (closeEditor) {
        setEditing(false);
      }
    }
  };

  return (
    <div className="odd:bg-white even:bg-gray-100 px-4 py-5 sm:grid sm:grid-cols-4 sm:gap-4 sm:px-6">
      <dt className="text-sm font-medium text-gray-500">
        {label}{' '}
        <Spin size="small" spinning={updating || !!loading} className="ml-2" />
      </dt>
      <dd className="mt-1 text-sm sm:mt-0 sm:col-span-3">
        {!editing ? (
          <div className="flex">
            {displayValue}
            {editable && (
              <Tooltip placement="top" title={t('common.editField')}>
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  className="ml-auto"
                  onClick={() => {
                    form.resetFields();
                    setEditing(true);
                  }}
                />
              </Tooltip>
            )}
          </div>
        ) : (
          <Form form={form} onFinish={(values) => submit(values)}>
            {formContent}
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={updating}
                disabled={updating || disableSave?.(watchedValues)}>
                {t('common.save')}
              </Button>
              <Button
                className="ml-2"
                onClick={() => {
                  form.resetFields();
                  setEditing(false);
                }}>
                {t('common.cancel')}
              </Button>
            </Form.Item>
          </Form>
        )}
      </dd>
    </div>
  );
};

export default AddressDetailsRow;
